// A product's variants (docs/claude/products.md, *Product variants*): the ways it
// is sold, each with its article number, power and price. A placed copy keeps a
// copy of the one the project uses. This module formats and parses powers,
// labels a variant, and reads the copy's variant and refreshes it from the
// catalogue. Pure and DOM-free (test/variants.test.mjs).

import type {
  Drawing,
  ProductInstance,
  ProductVariant,
  PropertyDef,
} from '../shared/types';

type Lang = 'en' | 'it' | 'fr';

/** A figure with up to two decimals, trailing zeros dropped; a comma in Italian
 *  and French. */
function figure(n: number, lang: Lang): string {
  const s = String(Math.round(n * 100) / 100);
  return lang === 'en' ? s : s.replace('.', ',');
}

/** A power in watts as shown: kW from 1000 W up ("36 kW", "18.75 kW"), else W. */
export function formatPower(w: number, lang: Lang = 'en'): string {
  return Math.abs(w) >= 1000 ? `${figure(w / 1000, lang)} kW` : `${figure(w, lang)} W`;
}

/** A power as typed, in watts: "36 kW", "750 W", "18,75 kW", or a bare number of
 *  watts. Null when it does not read as one. */
export function parsePower(text: string): number | null {
  const m = /^\s*([0-9]+(?:[.,][0-9]+)?)\s*(kw|w)?\s*$/i.exec(text);
  if (!m) return null;
  const n = parseFloat(m[1].replace(',', '.'));
  if (!Number.isFinite(n)) return null;
  return Math.round(m[2]?.toLowerCase() === 'kw' ? n * 1000 : n);
}

/** Whether a variant's power is entered: a new one starts at 0. */
const hasPower = (v: ProductVariant): boolean => typeof v.power === 'number' && Number.isFinite(v.power) && v.power > 0;

/** A variant as the panel's select lists it: "94 7829 · 36 kW · 400 V 3N~". */
export function variantLabel(v: ProductVariant, lang: Lang = 'en'): string {
  return [v.article?.trim(), hasPower(v) ? formatPower(v.power!, lang) : '', v.voltage?.trim()].filter(Boolean).join(' · ');
}

/** The range of powers a product is sold in, as its catalogue card shows it:
 *  "18–36 kW", one figure when they are all one; '' with none. */
export function variantRange(variants: readonly ProductVariant[] | undefined, lang: Lang = 'en'): string {
  const ws = (variants ?? []).filter(hasPower).map((v) => v.power!);
  if (!ws.length) return '';
  const lo = Math.min(...ws), hi = Math.max(...ws);
  if (lo === hi) return formatPower(lo, lang);
  if (lo >= 1000) return `${figure(lo / 1000, lang)}–${formatPower(hi, lang)}`;
  if (hi < 1000) return `${figure(lo, lang)}–${formatPower(hi, lang)}`;
  return `${formatPower(lo, lang)}–${formatPower(hi, lang)}`;
}

/** A product's properties with its variant's: each of the variant's added, or
 *  replacing the product's with the same key. */
export function effectiveProperties(product: { properties?: readonly PropertyDef[] }, variant?: ProductVariant | null): PropertyDef[] {
  const out = (product.properties ?? []).map((p) => ({ ...p }));
  for (const vp of variant?.properties ?? []) {
    const at = out.findIndex((p) => p.key === vp.key);
    if (at >= 0) out[at] = { ...vp };
    else out.push({ ...vp });
  }
  return out;
}

/** A variant's own copy, nothing shared with the one it was taken from. */
export function copyVariant(v: ProductVariant): ProductVariant {
  return JSON.parse(JSON.stringify(v)) as ProductVariant;
}

/** The variant a copy of `t` starts on: its first. */
export function firstVariant(variants: readonly ProductVariant[] | undefined): ProductVariant | undefined {
  return variants?.length ? copyVariant(variants[0]) : undefined;
}

/** A copy's variant refreshed from its product's list: the one with its id;
 *  kept as it is when its id is gone; the first when it has none yet (a copy
 *  placed before its product had variants). Undefined when neither has one. */
export function refreshVariant(variants: readonly ProductVariant[] | undefined, current: ProductVariant | undefined): ProductVariant | undefined {
  if (!variants?.length) return current;
  if (!current) return copyVariant(variants[0]);
  const same = variants.find((v) => v.id === current.id);
  return same ? copyVariant(same) : current;
}

/** The variant a placed copy uses: its own, else its product group's (a
 *  multi-part product keeps it on the group's `source`). */
export function placedVariant(drawing: Pick<Drawing, 'groups'>, inst: ProductInstance): ProductVariant | undefined {
  if (inst.variant) return inst.variant;
  const groups = drawing.groups ?? [];
  const seen = new Set<string>();
  let gid = inst.groupId;
  while (gid && !seen.has(gid)) {
    seen.add(gid);
    const g = groups.find((x) => x.id === gid);
    if (!g) break;
    if (g.source) return g.source.variant;
    gid = g.parent;
  }
  return undefined;
}

/** Unlink a placed multi-part product's group from its catalogue product (a
 *  per-part edit) without losing the variant the project uses: each part copy
 *  then carries it itself. */
export function unlinkProductGroup(drawing: Pick<Drawing, 'groups' | 'instances'>, groupId: string): void {
  const g = (drawing.groups ?? []).find((x) => x.id === groupId);
  if (!g?.source) return;
  const v = g.source.variant;
  if (v) for (const inst of drawing.instances) if (inst.groupId === groupId && !inst.variant) inst.variant = structuredClone(v);
  delete g.source;
}

/** What an electrical preparation's text becomes on a schedule: the variant's
 *  power and voltage added ("Power supply · 36 kW · 400 V 3N~"). */
export function preparationText(label: string, variant: ProductVariant | undefined, lang: Lang = 'en'): string {
  if (!variant) return label;
  const add = [hasPower(variant) ? formatPower(variant.power!, lang) : '', variant.voltage?.trim() ?? ''].filter(Boolean);
  return add.length ? [label, ...add].filter(Boolean).join(' · ') : label;
}
